import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { isFirebaseConfigured, firebaseProjectId } from "@/lib/firebase";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Signed in to Brugo Command Center");
      nav("/");
    } catch (err) {
      const code = err?.code ? err.code.replace("auth/", "").replace(/-/g, " ") : (err?.message || "sign in failed");
      console.error("Login error:", err?.code, err?.message);
      toast.error(`Sign in failed: ${code}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-secondary brugo-grid-bg overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <img src="/brugo-logo.png" alt="Brugo" className="relative h-12 w-auto self-start" />
        <div className="relative">
          <h2 className="font-heading text-5xl font-bold tracking-tight text-foreground leading-tight">
            One backend.<br />Every operation.
          </h2>
          <p className="mt-4 text-sm text-muted-foreground max-w-md">
            The operations control centre for the Brugo ecosystem — sharing the same
            Firebase backend as the Customer app and the Professional + Field app.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["KYC & Approvals", "Lead Release", "Projects & Promise", "Performance", "Incentives", "Payouts"].map((t) => (
              <span key={t} className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-muted-foreground">
          <span className={`h-2 w-2 rounded-full ${isFirebaseConfigured ? "bg-emerald-500" : "bg-amber-500"}`} />
          <span className="font-mono">{firebaseProjectId} · asia-south2</span>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-sm">
          <img src="/brugo-logo.png" alt="Brugo" className="h-9 w-auto mb-6 lg:hidden" />
          <h1 className="font-heading text-2xl font-bold text-foreground">Command Center</h1>
          <p className="mt-1 text-sm text-muted-foreground">Internal operations access only.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@brugo.in" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <Icons.ArrowRight className="h-4 w-4 ml-1.5" />}
            </Button>
          </form>

          <div className="mt-6 rounded-lg border border-border bg-secondary p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Firebase Authentication</p>
            <p>Sign in with your Brugo account on project <span className="font-mono">brugo-core-97965</span>.</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
